import type { DbProfile, UserRole, AccountType } from "./types";

function hasToDate(value: unknown): value is { toDate(): Date } {
  return (
    typeof value === "object" &&
    value !== null &&
    "toDate" in value &&
    typeof (value as { toDate?: unknown }).toDate === "function"
  );
}

function tsToIso(value: unknown): string {
  if (!value) return new Date(0).toISOString();
  if (hasToDate(value)) return value.toDate().toISOString();
  if (typeof value === "string") return value;
  if (value instanceof Date) return value.toISOString();
  return new Date(0).toISOString();
}

// Same as tsToIso but preserves "absent" as null. Required for the
// onboarding/tour gates: the epoch fallback above would make every user
// look like they completed the flow on 1970-01-01.
function tsToIsoOrNull(value: unknown): string | null {
  if (!value) return null;
  if (hasToDate(value)) return value.toDate().toISOString();
  if (typeof value === "string") return value;
  if (value instanceof Date) return value.toISOString();
  return null;
}

function str(v: unknown): string | null {
  return typeof v === "string" ? v : null;
}

function bool(v: unknown, fallback = false): boolean {
  return typeof v === "boolean" ? v : fallback;
}

export function userDocToProfile(
  uid: string,
  data: Record<string, unknown>,
): DbProfile {
  return {
    id: uid,
    email: (str(data.email) ?? "") as string,
    display_name: str(data.displayName),
    avatar_url: str(data.photoURL) ?? str(data.avatarUrl),
    role: (str(data.role) as UserRole) ?? "user",
    account_type: (str(data.accountType) as AccountType) ?? "individual",
    verified: bool(data.verified),
    bio: str(data.bio),
    website: str(data.website),
    institution_name: str(data.institutionName),
    stripe_customer_id: str(data.stripeCustomerId),
    institution_id: str(data.institutionId),
    enterprise_id: str(data.enterpriseId),
    enterprise_role:
      (str(data.enterpriseRole) as DbProfile["enterprise_role"]) ?? null,
    created_at: tsToIso(data.createdAt),
    updated_at: tsToIso(data.updatedAt),
    onboarding_completed_at: tsToIsoOrNull(data.onboardingCompletedAt),
    tour_completed_at: tsToIsoOrNull(data.tourCompletedAt),
    tour_skipped: bool(data.tourSkipped),
  };
}
